import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const STORAGE_KEY = 'kolv_cookie_consent_v1';

/**
 * GDPR / ePrivacy cookie consent banner.
 * Stores a JSON object in localStorage:
 *   { necessary: true, analytics: bool, marketing: bool, ts: ISO }
 * Other code can read this via `getCookieConsent()` to decide whether
 * to load GA4 / Meta Pixel / etc.
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true);
  }, []);

  const save = (payload) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...payload, ts: new Date().toISOString() })
      );
    } catch { /* storage blocked */ }
    setOpen(false);
  };

  if (!open) return null;

  const t = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  return (
    <div
      className="fixed inset-x-0 bottom-[60px] md:bottom-0 z-[70] p-3 sm:p-4 animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#E5E4E0] overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#D97736]/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-[#D97736]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-outfit font-semibold text-[#121212] text-base mb-1">
                {t('Mēs izmantojam sīkdatnes', 'We use cookies', 'Мы используем cookie')}
              </h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">
                {t(
                  'KoLV izmanto tikai tās sīkdatnes, kas nepieciešamas vietnes darbībai (pieslēgšanās sesija, maksājumi). Ar Tavu piekrišanu varam izmantot papildu sīkdatnes statistikai un satura uzlabošanai.',
                  'KoLV uses only the cookies necessary for the website to function (login session, payments). With your consent, we may use additional cookies for statistics and content improvements.',
                  'KoLV использует только те cookie, которые необходимы для работы сайта (сессия входа, платежи). С вашего согласия мы можем использовать дополнительные cookie для статистики и улучшения контента.'
                )}{' '}
                <Link
                  to="/sikdatnu-politika"
                  className="text-[#D97736] underline hover:no-underline"
                  data-testid="cookie-policy-link"
                >
                  {t('Uzzināt vairāk', 'Learn more', 'Узнать больше')}
                </Link>
              </p>

              {showSettings && (
                <div className="mt-4 space-y-3 p-3 rounded-xl bg-[#FAFAFA] border border-[#E5E4E0]">
                  <label className="flex items-start gap-3 text-sm cursor-not-allowed opacity-70">
                    <input type="checkbox" checked readOnly className="mt-0.5" />
                    <span>
                      <span className="font-medium text-[#121212]">
                        {t('Nepieciešamās', 'Necessary', 'Необходимые')}
                      </span>{' '}
                      <span className="text-[#8A8A8A]">
                        ({t('vienmēr aktīvas', 'always active', 'всегда активны')})
                      </span>
                      <p className="text-xs text-[#5C5C5C]">
                        {t(
                          'Autorizācijas sesija, groza saturs, drošība. Bez tām vietne nedarbojas.',
                          'Login session, cart contents, security. Without these the site does not work.',
                          'Сессия авторизации, содержимое корзины, безопасность. Без них сайт не работает.'
                        )}
                      </p>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="cookie-toggle-analytics">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium text-[#121212]">
                        {t('Analītiskās', 'Analytics', 'Аналитические')}
                      </span>
                      <p className="text-xs text-[#5C5C5C]">
                        {t(
                          'Anonīma statistika par to, kā apmeklētāji lieto vietni (piemēram, Google Analytics).',
                          'Anonymous statistics on how visitors use the site (e.g. Google Analytics).',
                          'Анонимная статистика о том, как посетители используют сайт (например, Google Analytics).'
                        )}
                      </p>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="cookie-toggle-marketing">
                    <input
                      type="checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium text-[#121212]">
                        {t('Mārketinga', 'Marketing', 'Маркетинговые')}
                      </span>
                      <p className="text-xs text-[#5C5C5C]">
                        {t(
                          'Reklāmas efektivitātes mērīšana un satura personalizācija (piemēram, Meta Pixel).',
                          'Ad performance measurement and content personalization (e.g. Meta Pixel).',
                          'Измерение эффективности рекламы и персонализация контента (например, Meta Pixel).'
                        )}
                      </p>
                    </span>
                  </label>
                </div>
              )}
            </div>
            <button
              onClick={() => save({ necessary: true, analytics: false, marketing: false })}
              className="text-[#8A8A8A] hover:text-[#121212] transition-colors"
              aria-label={t('Aizvērt', 'Close', 'Закрыть')}
              data-testid="cookie-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
            {!showSettings ? (
              <button
                onClick={() => setShowSettings(true)}
                className="text-sm text-[#5C5C5C] hover:text-[#121212] px-4 py-2 transition-colors"
                data-testid="cookie-customize-btn"
              >
                {t('Iestatījumi', 'Customize', 'Настройки')}
              </button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => save({ necessary: true, analytics: false, marketing: false })}
              className="border-[#E5E4E0] text-[#5C5C5C] hover:bg-[#FAFAFA]"
              data-testid="cookie-necessary-only-btn"
            >
              {t('Tikai nepieciešamās', 'Only necessary', 'Только необходимые')}
            </Button>
            <Button
              onClick={() =>
                save({
                  necessary: true,
                  analytics: showSettings ? analytics : true,
                  marketing: showSettings ? marketing : true,
                })
              }
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid="cookie-accept-all-btn"
            >
              {showSettings
                ? t('Saglabāt izvēli', 'Save choice', 'Сохранить выбор')
                : t('Piekrītu visām', 'Accept all', 'Принять все')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
